/**
 * Product catalog: language-independent facts about everything the site
 * presents (status, audience, outbound links). Copy lives in base/content.
 */

export type Audience = "developers" | "business" | "apps";

export type ProductStatus =
  | "published"
  | "stable"
  | "experimental"
  | "early-access"
  | "coming-soon";

export interface Product {
  id: string;
  name: string;
  audiences: readonly Audience[];
  status: ProductStatus;
  /** Page on codecious.com, if any. */
  path?: string;
  links: Readonly<Record<string, string>>;
}

export const PRODUCTS = [
  {
    id: "gymtero",
    name: "Gymtero",
    audiences: ["apps"],
    status: "published",
    path: "/apps/gymtero",
    links: {
      web: "https://gymtero.com",
      playStore:
        "https://play.google.com/store/apps/details?id=com.codecious.gymtero",
    },
  },
  {
    id: "nutrinima",
    name: "Nutrinima",
    audiences: ["apps"],
    status: "coming-soon",
    path: "/apps/nutrinima",
    links: {},
  },
  {
    id: "jobxtech",
    name: "Jobxtech",
    // Candidates are end users; recruiters are businesses.
    audiences: ["apps", "business"],
    status: "coming-soon",
    path: "/apps/jobxtech",
    links: {},
  },
  {
    id: "distribute",
    name: "Codecious Distribute",
    audiences: ["business"],
    status: "early-access",
    links: {},
  },
  {
    id: "bootgly",
    name: "Bootgly",
    audiences: ["developers"],
    status: "stable",
    // Has its own site and docs: codecious.com only links out.
    links: {
      site: "https://bootgly.com",
      docs: "https://docs.bootgly.com",
      github: "https://github.com/bootgly/bootgly",
      packagist: "https://packagist.org/packages/bootgly/bootgly",
    },
  },
  {
    id: "coreuix",
    name: "CoreUIX",
    audiences: ["developers"],
    status: "stable",
    links: { npm: "https://www.npmjs.com/package/@codecious/coreuix" },
  },
  {
    id: "dataruni",
    name: "Dataruni",
    audiences: ["developers"],
    status: "experimental",
    links: {
      npm: "https://www.npmjs.com/package/@codecious/dataruni",
      github: "https://github.com/CodeciousLTDA/Dataruni",
    },
  },
  {
    id: "codecious-build",
    name: "Codecious Build",
    audiences: ["developers"],
    status: "coming-soon",
    links: {},
  },
] as const satisfies readonly Product[];

export type ProductId = (typeof PRODUCTS)[number]["id"];

export function getProduct(id: ProductId): Product {
  return PRODUCTS.find(product => product.id === id) as Product;
}
